import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Radio, Select, Space, Spin, Typography } from "antd";

import { ancanaApi } from "../../../api/ancanaApi";
import DevelopmentSlidePanel from "./DevelopmentSlidePanel";

/**
 * Link a property to a development, existing or new, from the pre-generation
 * checklist — the one step there that used to send people to another page.
 *
 * Writes through `housing-developments/<id>/link/`, which adds this one
 * property and leaves the development's others alone. A new development needs
 * only a name; the rest of its profile is the slide panel, which opens right
 * after, since an empty development leaves the slide out anyway.
 *
 * `onLinked` waits for the modal to close: the checklist it refreshes turns
 * this step's button into "Complete it", which would unmount the modal while
 * the new development's slide is still open in it.
 */

interface DevelopmentOption {
  id: number;
  name: string;
  listings: { id: number }[];
}

type Mode = "existing" | "new";

function LinkDevelopmentModal({ listingId, listingName, children, onLinked }: {
  listingId: number;
  listingName: string;
  children: React.ReactNode;
  onLinked?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("existing");
  const [options, setOptions] = useState<DevelopmentOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<number>();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  /** A development created here, whose slide is shown next. */
  const [created, setCreated] = useState<DevelopmentOption>();
  const [linked, setLinked] = useState(false);

  const search = useCallback((query?: string) => {
    setSearching(true);
    ancanaApi.get<{ results: DevelopmentOption[] }>("housing-developments/", {
      params: { search: query || undefined, fields: "id,name,listings", page_size: 50 },
    })
      .then(({ data }) => setOptions(data.results))
      .catch(() => setError("Could not load the developments."))
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    if (open) search();
  }, [open, search]);

  const close = () => {
    setOpen(false);
    setMode("existing");
    setPicked(undefined);
    setName("");
    setError(undefined);
    setCreated(undefined);
    if (linked) {
      setLinked(false);
      onLinked?.();
    }
  };

  const link = (developmentId: number) =>
    ancanaApi.post(`housing-developments/${developmentId}/link/`, { listing: listingId });

  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      if (mode === "existing" && picked) {
        await link(picked);
        close();
        onLinked?.();
      } else if (mode === "new" && name.trim()) {
        const { data } = await ancanaApi.post<DevelopmentOption>(
          "housing-developments/", { name: name.trim() });
        await link(data.id);
        setLinked(true);
        setCreated(data);
      }
    } catch (e: any) {
      const body = e?.response?.data;
      setError(body?.name ?? body?.listing ?? "Could not link the development.");
    } finally {
      setSaving(false);
    }
  };

  const ready = mode === "existing" ? !!picked : !!name.trim();

  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>
      <Modal
        visible={open}
        onCancel={close}
        destroyOnClose
        width={created ? 720 : 520}
        title={created ? `Development slide — ${created.name}` : `Link ${listingName} to a development`}
        footer={created
          ? [<Button key="done" type="primary" onClick={close}>Done</Button>]
          : [
            <Button key="cancel" onClick={close}>Cancel</Button>,
            <Button key="link" type="primary" disabled={!ready} loading={saving} onClick={save}>
              {mode === "new" ? "Create and link" : "Link"}
            </Button>,
          ]}
      >
        {created ? (
          <DevelopmentSlidePanel developmentId={created.id} />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {error && <Alert type="error" message={String(error)} />}
            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
              <Radio value="existing">Existing development</Radio>
              <Radio value="new">New development</Radio>
            </Radio.Group>
            {mode === "existing" ? (
              <Select
                showSearch
                style={{ width: "100%" }}
                placeholder="Search developments"
                aria-label="development"
                value={picked}
                onChange={(id: number) => setPicked(id)}
                onSearch={search}
                filterOption={false}
                notFoundContent={searching ? <Spin size="small" /> : null}
                options={options.map((development) => ({
                  value: development.id,
                  label: `${development.name} · ${development.listings.length} `
                    + (development.listings.length === 1 ? "property" : "properties"),
                }))}
              />
            ) : (
              <Input
                placeholder="Development name"
                aria-label="development name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onPressEnter={() => ready && save()}
              />
            )}
            <Typography.Text type="secondary">
              The link belongs to the property, so every brochure with a development
              slide uses it, not only this template's.
            </Typography.Text>
          </Space>
        )}
      </Modal>
    </>
  );
}

export default LinkDevelopmentModal;
